import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Compass,
  Sparkles,
  BookOpen,
  Award,
  Activity,
  FileText,
  HelpCircle,
  TrendingUp,
  Map,
  ShieldAlert,
  Search,
  Gem,
  Calendar,
  Layers,
  ArrowRight,
  Printer,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Info,
  Clock,
  Loader2
} from 'lucide-react';
import { CompleteKundali, Language } from '../types/kundali';
import {
  LifeDomain,
  AnalysisMode,
  DOMAIN_FRAMEWORKS,
  prepareClassicalFoundation,
  JyotishaAnalysisResult
} from '../utils/vedicAnalysisEngine';

interface VedicAnalysisEngineProps {
  kundali: CompleteKundali | null;
  lang: Language;
}

function getConfidenceBadge(level?: string) {
  const normalized = (level || '').toUpperCase();
  if (normalized.includes('HIGH') || normalized.includes('VERY HIGH')) {
    return {
      symbol: '🟢',
      label: 'HIGH CONFIDENCE',
      badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200'
    };
  }
  if (normalized.includes('MODERATE') || normalized.includes('MEDIUM')) {
    return {
      symbol: '🟡',
      label: 'MODERATE CONFIDENCE',
      badgeClass: 'bg-astro-secondary-surface text-astro-secondary border-[#E2DED5]'
    };
  }
  if (normalized.includes('LOW')) {
    return {
      symbol: '🟠',
      label: 'LOW CONFIDENCE',
      badgeClass: 'bg-orange-50 text-orange-800 border-orange-200'
    };
  }
  return {
    symbol: '⚪',
    label: 'INCONCLUSIVE',
    badgeClass: 'bg-astro-secondary-surface text-astro-secondary-text border-astro-border'
  };
}

export default function VedicAnalysisEngine({ kundali, lang }: VedicAnalysisEngineProps) {
  const [domain, setDomain] = useState<LifeDomain>('CAREER');
  const [mode, setMode] = useState<AnalysisMode>('INTEGRATED');
  const [question, setQuestion] = useState('When will I experience a major career breakthrough and expansion?');
  const [customQuestion, setCustomQuestion] = useState('');
  const [activeAnalysis, setActiveAnalysis] = useState<JyotishaAnalysisResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Detailed analysis view active sub-tab
  const [subTab, setSubTab] = useState<'promise' | 'varga' | 'strength' | 'timing' | 'transit' | 'synthesis'>('promise');

  // Progressive Disclosure states (Hide technical complexity by default)
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [showGemTechnical, setShowGemTechnical] = useState(false);
  const [showMuhurtaTechnical, setShowMuhurtaTechnical] = useState(false);

  // Gemstone evaluation state
  const [gemSelectedPlanet, setGemSelectedPlanet] = useState('Jupiter');

  // Muhurta state
  const [muhurtaActivity, setMuhurtaActivity] = useState('Marriage');

  // Question templates mapped to domains
  const QUESTION_TEMPLATES: { [key in LifeDomain]: string[] } = {
    MARRIAGE: [
      'When will I get married and how will my spouse be?',
      'Is there delay or denial of marriage in my chart?',
      'How will my married life be in terms of harmony?'
    ],
    CAREER: [
      'When will I experience a major career breakthrough and expansion?',
      'Is a government job or authoritative leadership promised in my chart?',
      'Should I stay in my current job or look for a transition?'
    ],
    BUSINESS: [
      'Is independent business promised in my chart or should I stick to service?',
      'Will partnership business be beneficial or cause conflicts?',
      'When is the most auspicious dasha to launch my business venture?'
    ],
    WEALTH: [
      'Does my chart form strong Dhana Yogas for self-made wealth?',
      'When will my financial struggles end and prosperity begin?',
      'Which planetary periods will trigger major material gains?'
    ],
    PROPERTY: [
      'When will I be able to buy my own house or property?',
      'Is there any promise of ancestral property inheritance?',
      'Will property investments yield high returns or legal disputes?'
    ],
    CHILDREN: [
      'Does my chart promise the joy of parenthood?',
      'When is a supportive timing window for child conception?',
      'How will my children do in their lives and what is their relationship with me?'
    ],
    EDUCATION: [
      'Will I be successful in higher academic pursuits or research?',
      'Is foreign education or specialization promised in my chart?',
      'Which stream of study is most aligned with my planetary strengths?'
    ],
    FOREIGN_TRAVEL: [
      'Is permanent foreign settlement promised in my chart?',
      'When will I travel abroad for short-term projects or leisure?',
      'Will relocation to another city/country bring auspicious results?'
    ],
    SPIRITUALITY: [
      'Does my chart indicate strong spiritual initiation (Moksha/Sannyasa)?',
      'Which deity or spiritual path (Ishta Devata) is aligned with my Atmakaraka?',
      'When will I experience spiritual awakening or find my guru?'
    ],
    FAME_LEADERSHIP: [
      'Am I destined for public fame, leadership, and recognition?',
      'Does my chart form powerful Raja Yogas or VIP status?',
      'When will my contributions be recognized by society?'
    ],
    HEALTH: [
      'What are the key health-related vulnerabilities indicated in my chart?',
      'When will I recover from my current physiological or mental stress?',
      'Are there strong longevity and vitality indicators in my chart?'
    ],
    OTHER: [
      'What is the overall destiny and karmic lesson of my life?',
      'How will my upcoming major dasha transition affect me?',
      'What are the primary strengths and weaknesses I should balance?'
    ]
  };

  // Perform multi-model consensus calculations
  const calculateAnalysis = async () => {
    if (!kundali) {
      setErrorMsg(lang === 'hi' ? 'कृपया पहले एक कुंडली लोड करें या बनाएं।' : 'Please load or create a Kundali first.');
      return;
    }
    setErrorMsg('');
    setIsCalculating(true);

    const activeQuestion = customQuestion.trim() || question;

    // 1. Calculate traditional baseline engine values
    const baseline = prepareClassicalFoundation(kundali, domain, mode, activeQuestion);

    try {
      // 2. Feed into divine AI expert engine for deep scholarly sutra synthesis (if configured)
      const res = await fetch('/api/vedic/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birthDetails: kundali.birthDetails,
          domain,
          mode,
          question: activeQuestion,
          birthTimeUncertaintyMinutes: 5
        })
      });

      if (res.ok) {
        const data = await res.json();
        setActiveAnalysis(data.result);
      } else {
        // Fallback to offline traditional engine
        setActiveAnalysis(baseline);
      }
    } catch (err) {
      console.error(err);
      setActiveAnalysis(baseline);
    } finally {
      setIsCalculating(false);
    }
  };

  // Keep question state synced when domain template shifts
  useEffect(() => {
    setQuestion(QUESTION_TEMPLATES[domain][0]);
    setCustomQuestion('');
  }, [domain]);

  // Run calculation immediately once kundali or domain shifts to maintain instant working state
  useEffect(() => {
    if (kundali) {
      calculateAnalysis();
    }
  }, [kundali, domain, mode]);

  const handlePrint = () => {
    if (!activeAnalysis) return;
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    printWin.document.write(`
      <html>
        <head>
          <title>Vedanga Jyotisha Multi-Model Analytical Report</title>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
          <style>
            @media print {
              body { font-size: 12px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body class="bg-white text-astro-text p-8">
          <div class="max-w-4xl mx-auto space-y-6">
            <div class="border-b-2 border-amber-900 pb-4 text-center">
              <h1 class="text-3xl font-extrabold uppercase tracking-widest text-astro-secondary">Janma Patrika Shodhana</h1>
              <p class="text-sm text-astro-muted mt-1">Authentic Event-Specific Multi-Model Analytical Report</p>
            </div>

            <div class="grid grid-cols-2 gap-4 text-sm bg-astro-secondary-surface p-4 rounded-xl border border-astro-border">
              <div>
                <p><strong>Query:</strong> ${activeAnalysis.question}</p>
                <p><strong>Domain:</strong> ${activeAnalysis.domain}</p>
                <p><strong>Mode:</strong> ${activeAnalysis.mode}</p>
              </div>
              <div class="text-right">
                <p><strong>Natal Promise Evaluation:</strong> ${typeof activeAnalysis.natalPromiseScore === 'number' && Number.isFinite(activeAnalysis.natalPromiseScore) ? `${activeAnalysis.natalPromiseScore}%` : 'Classical Placements & Dignities'}</p>
                <p><strong>Timing Evaluation:</strong> ${typeof activeAnalysis.timingScore === 'number' && Number.isFinite(activeAnalysis.timingScore) ? `${activeAnalysis.timingScore}%` : 'Vimshottari Dasha Timeline'}</p>
                <p><strong>Confidence:</strong> ${activeAnalysis.confidenceLevel}</p>
              </div>
            </div>

            ${activeAnalysis.aiContradictionDetected ? `
            <div style="background-color: #fef3c7; border: 1px solid #f59e0b; padding: 12px; border-radius: 8px; font-size: 12px; color: #78350f;">
              <strong>Classical Baseline Notice:</strong> Unverified AI commentary was suppressed because narrative contradicted verified astronomical chart placements or violated epistemic safety rules. Displaying authentic classical Jyotish calculations.
            </div>` : ''}

            ${activeAnalysis.isConfigurationSensitive ? `
            <div style="background-color: #e0f2fe; border: 1px solid #0284c7; padding: 12px; border-radius: 8px; font-size: 12px; color: #0369a1;">
              <strong>Ascendant Boundary Sensitivity Notice:</strong> Recorded birth time is close to a zodiac sign boundary within the uncertainty interval (±${activeAnalysis.sensitivityDetails?.uncertaintyMinutes || 5} min). Minor time adjustments may alter divisional charts.
            </div>` : ''}

            <div class="space-y-4">
              <h3 class="text-xl font-bold text-[#263633] border-b border-astro-border pb-1">Direct Scholarly Verdict</h3>
              <p class="text-astro-text leading-relaxed italic">"${activeAnalysis.directAnswer}"</p>
            </div>

            <div class="space-y-4">
              <h3 class="text-xl font-bold text-[#263633] border-b border-astro-border pb-1">Primary Analytical Justifications</h3>
              <ul class="list-disc pl-5 space-y-2 text-astro-secondary-text">
                ${activeAnalysis.whyPoints.map(pt => `<li>${pt}</li>`).join('')}
              </ul>
            </div>

            <div class="space-y-4">
              <h3 class="text-xl font-bold text-[#263633] border-b border-astro-border pb-1">Detailed Technical Layers</h3>
              <div class="space-y-3 text-sm">
                <p><strong>D1 Natal Foundation:</strong> ${activeAnalysis.detailedAnalysis.d1Analysis}</p>
                <p><strong>Varga (Divisional) Refinement:</strong> ${activeAnalysis.detailedAnalysis.vargaAnalysis}</p>
                <p><strong>Planetary Strength Evaluation:</strong> ${activeAnalysis.detailedAnalysis.strengthAnalysis}</p>
                <p><strong>Dasha Timing Matrix:</strong> ${activeAnalysis.detailedAnalysis.dashaTimingAnalysis}</p>
                <p><strong>Gochar (Transit) Confirmation:</strong> ${activeAnalysis.detailedAnalysis.transitConfirmation}</p>
                <p><strong>Synthesis:</strong> ${activeAnalysis.detailedAnalysis.synthesis}</p>
              </div>
            </div>

            <div class="mt-8 border-t border-astro-border pt-4 text-center text-xs text-astro-muted">
              Generated by Vedanga Jyotisha Multi-Model Analytical Suite. All rules authenticated from Brihat Parashara Hora Shastra & Jaimini Sutras.
            </div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWin.document.close();
  };

  // Gemstone Evaluation logic obeying strict rules: ascendant, lordship, houses, shadbala, risk
  const getGemstoneEvaluation = (planet: string) => {
    const gemMapping: { [key: string]: { name: string; lordships: string; benefit: string; risk: string; nonGem: string } } = {
      Sun: {
        name: 'Ruby (Manikya)',
        lordships: 'Rules soul, leadership, and public authority.',
        benefit: 'Strengthens self-confidence, vital immune system, and professional fame.',
        risk: 'If Sun owns Dusthanas (6/8/12), wearing Ruby can trigger extreme ego, blood pressure spikes, and conflicts with superiors.',
        nonGem: 'Offer water to the rising Sun (Arghya) and chant Gayatri Mantra.'
      },
      Moon: {
        name: 'Pearl (Moti)',
        lordships: 'Rules emotions, mother, and mental serenity.',
        benefit: 'Balances emotional fluctuations, cures sleep disorders, and brings inner peace.',
        risk: 'If Moon is afflicted or rules the 6th/8th house, Pearl can cause deep depression, cold-related illnesses, and extreme mood swings.',
        nonGem: 'Chant "Om Namah Shivaya" and serve mother or maternal figures.'
      },
      Mars: {
        name: 'Red Coral (Moonga)',
        lordships: 'Rules physical vitality, courage, and land/real estate.',
        benefit: 'Boosts ambition, removes fear, and supports land transactions.',
        risk: 'If Mars is a functional malefic, Coral can cause accident vulnerability, severe anger outbursts, and marital discord.',
        nonGem: 'Recite Hanuman Chalisa on Tuesdays and donate lentils.'
      },
      Mercury: {
        name: 'Emerald (Panna)',
        lordships: 'Rules intelligence, commerce, speech, and analysis.',
        benefit: 'Enhances business acumen, logic, speech clarity, and memory power.',
        risk: 'If Mercury is heavily combust or placed in Dusthanas, Emerald can trigger nervous anxiety and commercial loss.',
        nonGem: 'Feed green grass or vegetables to cows on Wednesdays.'
      },
      Jupiter: {
        name: 'Yellow Sapphire (Pukhraj)',
        lordships: 'Rules wisdom, wealth, children, and spiritual expansion.',
        benefit: 'Triggers wealth accumulation, higher education success, and marital harmony for women.',
        risk: 'If Jupiter is functional malefic (e.g. Taurus/Libra ascendants), it can trigger liver issues, excessive weight gain, and over-optimistic financial blunders.',
        nonGem: 'Respect teachers and elders, and chant Guru Mantra.'
      },
      Venus: {
        name: 'Diamond (Heera) / White Sapphire',
        lordships: 'Rules marital bliss, luxury, artistic talents, and romance.',
        benefit: 'Brings aesthetic comfort, vehicle luxuries, and strong marital attraction.',
        risk: 'If Venus rules the 6th or 8th house, Diamond can trigger extreme sensual overindulgence, financial debt, and kidney diseases.',
        nonGem: 'Donate white clothes or sweets to women in need.'
      },
      Saturn: {
        name: 'Blue Sapphire (Neelam)',
        lordships: 'Rules discipline, longevity, career structures, and delays.',
        benefit: 'Rapidly resolves career stagnation and brings structure and legal victory.',
        risk: 'NEVER wear without a trial. If Saturn is functionally unfavorable, Neelam can cause sudden heavy financial downfalls, accidents, and psychological dread within 48 hours.',
        nonGem: 'Light mustard oil lamps under a Peepal tree on Saturdays.'
      }
    };
    return gemMapping[planet] || gemMapping['Jupiter'];
  };

  // Muhurta selector rules based on selected activity
  const getMuhurtaRules = (activity: string) => {
    const rules: { [key: string]: { tithis: string; naks: string; lagnas: string; prohibitions: string } } = {
      Marriage: {
        tithis: 'Dwitaya (2nd), Tritiya (3rd), Panchami (5th), Saptami (7th), Ekadashi (11th), Trayodashi (13th)',
        naks: 'Rohini, Mrigashirsha, Uttaraphalguni, Hasta, Anuradha, Uttarashadha, Revati',
        lagnas: 'Jupiter or Venus placed in kendras, 7th house must be empty of all planets.',
        prohibitions: 'Avoid Rikta Tithis (4/9/14) and eclipse periods completely.'
      },
      'Property registration': {
        tithis: 'Dwitaya (2nd), Panchami (5th), Saptami (7th), Dashami (10th), Ekadashi (11th)',
        naks: 'Mrigashirsha, Hasta, Chitra, Anuradha, Revati, Pushya',
        lagnas: 'Mars must be highly dignified and not combust in D1/D4.',
        prohibitions: 'Avoid Mars retrograde periods.'
      },
      'Business launch': {
        tithis: 'Tritiya (3rd), Saptami (7th), Dashami (10th), Ekadashi (11th), Purnima (15th)',
        naks: 'Rohini, Pushya, Uttaraphalguni, Hasta, Anuradha, Shravana',
        lagnas: 'Ascendant lord placed in 11th or 10th house, Mercury free from affliction.',
        prohibitions: 'Do not launch business during Rahu Kaal or combust Mercury periods.'
      },
      'Vehicle purchase': {
        tithis: 'Dwitaya (2nd), Tritiya (3rd), Panchami (5th), Saptami (7th), Dashami (10th), Ekadashi (11th)',
        naks: 'Rohini, Punarvasu, Hasta, Chitra, Swati, Shravana, Shatabhisha',
        lagnas: '4th house must be aspected by benefics, Venus must be strong.',
        prohibitions: 'Avoid Saturn transits aspecting the 4th house Lord.'
      }
    };
    return rules[activity] || rules['Marriage'];
  };

  const gemEval = getGemstoneEvaluation(gemSelectedPlanet);
  const muhRules = getMuhurtaRules(muhurtaActivity);

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-6 space-y-6" id="vedic_analysis_engine_root">
      {/* Visual Title Banner */}
      <div className="bg-gradient-to-r from-amber-900 via-amber-950 to-orange-950 text-white rounded-2xl p-6 md:p-8 shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Compass className="w-52 h-52 animate-spin-slow" />
        </div>
        <div className="max-w-2xl relative z-10 space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-amber-200 text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> Vedanga Sastra
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">
            Siddhanta Jyotisha Engine
          </h1>
          <p className="text-amber-100 text-sm md:text-base leading-relaxed">
            Scholarly Event-Specific Multi-Model Analysis. Assess Natal Promise, Divisional Vargas, Shadbala planetary strengths, Vimshottari timings, and Transit triggers with high precision.
          </p>
        </div>
      </div>

      {!kundali ? (
        <div className="bg-astro-bg border border-astro-border rounded-xl p-8 text-center max-w-lg mx-auto space-y-4">
          <div className="p-3 bg-astro-secondary-surface rounded-full w-12 h-12 mx-auto flex items-center justify-center text-astro-secondary">
            <HelpCircle className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-astro-text">No Active Natal Chart</h3>
          <p className="text-sm text-astro-secondary-text">
            Please enter your birth parameters or select a saved Kundali in the main workspace to activate the event-specific Siddhanta engine.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Form & Configuration */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-astro-bg border border-astro-border rounded-xl p-5 md:p-6 space-y-5 shadow-sm">
              <h3 className="font-bold text-[#263633] text-base flex items-center gap-1.5 border-b border-astro-border pb-2">
                <Layers className="w-4.5 h-4.5 text-astro-secondary" /> Analysis Blueprint
              </h3>

              {/* Step 1: Select Domain */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                  1. Life Domain Focus
                </label>
                <select
                  value={domain}
                  onChange={e => setDomain(e.target.value as LifeDomain)}
                  className="w-full px-3 py-2 bg-white border border-astro-border rounded-lg text-sm text-astro-text focus:outline-none focus:ring-1 focus:ring-amber-900"
                >
                  <option value="CAREER">Career & Profession</option>
                  <option value="MARRIAGE">Marriage & Alliance</option>
                  <option value="BUSINESS">Business & Commerce</option>
                  <option value="WEALTH">Wealth & Dhana Yogas</option>
                  <option value="PROPERTY">Property & Land (D4)</option>
                  <option value="CHILDREN">Children & Saptamsha (D7)</option>
                  <option value="EDUCATION">Education & Vidya (D24)</option>
                  <option value="FOREIGN_TRAVEL">Foreign Journey & Settlement</option>
                  <option value="SPIRITUALITY">Spirituality & Vimshamsha</option>
                  <option value="FAME_LEADERSHIP">Fame & Public Leadership</option>
                  <option value="HEALTH">Health & Shashtamsha (D6)</option>
                  <option value="OTHER">Other Custom Life Events</option>
                </select>
              </div>

              {/* Step 2: Select Mode */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                  2. Traditional Framework
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'PARASHARI', label: 'Parashari', desc: 'Core D1/D9' },
                    { id: 'JAIMINI', label: 'Jaimini', desc: 'Chara Karakas' },
                    { id: 'INTEGRATED', label: 'Integrated', desc: 'All Models' }
                  ].map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMode(m.id as AnalysisMode)}
                      className={`p-2.5 rounded-lg border text-center transition-all ${
                        mode === m.id
                          ? 'border-amber-900 bg-astro-secondary-surface text-[#263633] font-bold'
                          : 'border-astro-border bg-white hover:border-astro-border text-astro-secondary-text'
                      }`}
                    >
                      <span className="text-xs block">{m.label}</span>
                      <span className="text-[9px] text-astro-muted font-medium">{m.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Choose Question */}
              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                  3. Select Query Template
                </label>
                <div className="space-y-2">
                  {QUESTION_TEMPLATES[domain].map(temp => (
                    <button
                      key={temp}
                      onClick={() => {
                        setQuestion(temp);
                        setCustomQuestion('');
                      }}
                      className={`w-full text-left p-2.5 rounded-lg border text-xs leading-relaxed transition-all ${
                        question === temp && !customQuestion
                          ? 'bg-astro-secondary text-white border-amber-900'
                          : 'bg-white text-astro-secondary-text border-astro-border hover:bg-astro-secondary-surface'
                      }`}
                    >
                      {temp}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom question input */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                  Or Type Custom Query
                </label>
                <textarea
                  value={customQuestion}
                  onChange={e => setCustomQuestion(e.target.value)}
                  placeholder="Ask a custom divine event query..."
                  className="w-full px-3 py-2 border border-astro-border rounded-lg text-xs bg-white focus:outline-none min-h-[70px]"
                />
              </div>

              <button
                onClick={calculateAnalysis}
                disabled={isCalculating}
                className="w-full py-2.5 bg-astro-secondary hover:bg-amber-800 text-white font-bold rounded-lg text-sm transition-all flex items-center justify-center gap-2 shadow"
              >
                {isCalculating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Consolidating Sutras...
                  </>
                ) : (
                  <>
                    <Compass className="w-4 h-4" /> Align Classical Models
                  </>
                )}
              </button>

              {errorMsg && (
                <div className="p-3 bg-red-50 text-red-800 rounded-lg text-xs font-medium">
                  {errorMsg}
                </div>
              )}
            </div>

            {/* Quick Traditional Sources Panel */}
            <div className="bg-astro-bg border border-astro-border rounded-xl p-5 space-y-4 shadow-sm">
              <h4 className="font-bold text-[#263633] text-xs uppercase tracking-wider border-b border-astro-border pb-2 flex items-center gap-1.5">
                <BookOpen className="w-4 h-4 text-astro-secondary" /> Sastra Citations
              </h4>
              <div className="space-y-3">
                <div className="space-y-0.5">
                  <span className="text-[10px] font-bold text-astro-secondary uppercase">Brihat Parashara Hora Shastra</span>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                    Chapters 9-11 detail the evaluation of the Ascendant, Bhavesha placements, and the specific refinement of Navamsha (D9).
                  </p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] font-bold text-astro-secondary uppercase">Upadesha Sutras of Jaimini Maharsi</span>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                    Sutras concerning Atmakaraka, Chara Karakas, Swamsha Lagna and Arudha Pada manifestations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Center & Right Columns: Consolidate analysis results */}
          <div className="lg:col-span-2 space-y-6">
            <AnimatePresence mode="wait">
              {activeAnalysis && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  {/* DEFAULT USER EXPERIENCE: CLEAN, NON-TECHNICAL HIGH-LEVEL OUTCOME */}
                  <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 md:p-8 shadow-sm space-y-6">
                    {/* Header & Confidence Badge */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-astro-border pb-4">
                      <div>
                        <span className="text-xs font-bold text-astro-secondary uppercase tracking-wider">
                          Final Result — {domain.replace('_', ' ')}
                        </span>
                        <h2 className="text-2xl font-extrabold text-astro-text font-display mt-1">
                          {typeof activeAnalysis.natalPromiseScore === 'number' && Number.isFinite(activeAnalysis.natalPromiseScore)
                            ? (activeAnalysis.natalPromiseScore > 80 ? 'Outlook: Favourable' : activeAnalysis.natalPromiseScore > 60 ? 'Outlook: Moderately Favourable' : 'Outlook: Requires Caution')
                            : 'Classical Qualitative Assessment'}
                        </h2>
                      </div>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const conf = getConfidenceBadge(activeAnalysis.confidenceLevel);
                          return (
                            <span className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border ${conf.badgeClass}`}>
                              <span>{conf.symbol}</span>
                              <span>{conf.label}</span>
                            </span>
                          );
                        })()}
                        <button
                          onClick={handlePrint}
                          className="px-3 py-1 border border-astro-border hover:border-astro-border rounded-lg text-xs font-semibold text-astro-secondary-text bg-white flex items-center gap-1 transition"
                        >
                          <Printer className="w-3.5 h-3.5" /> Export
                        </button>
                      </div>
                    </div>

                    {/* High-Level Interpretation */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted">
                        High-Level Interpretation
                      </h3>
                      <p className="text-base font-medium text-astro-text leading-relaxed">
                        {activeAnalysis.directAnswer}
                      </p>
                    </div>

                    {/* Timing Section if applicable */}
                    <div className="p-4 bg-astro-secondary-surface border border-[#E2DED5] rounded-xl space-y-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-[#263633] flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-astro-secondary" /> Favourable Timing Windows
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 text-xs">
                        <div>
                          <span className="text-astro-muted block font-medium">Primary Favourable Window:</span>
                          <strong className="text-astro-text font-bold text-sm">
                            {typeof activeAnalysis.timingScore === 'number' && Number.isFinite(activeAnalysis.timingScore) && activeAnalysis.timingScore > 75
                              ? 'Current Active Cycle (Next 6 – 18 Months)'
                              : 'Primary Vimshottari Window'}
                          </strong>
                        </div>
                        <div>
                          <span className="text-astro-muted block font-medium">Secondary Activation Window:</span>
                          <strong className="text-astro-text font-bold text-sm">
                            {typeof activeAnalysis.timingScore === 'number' && Number.isFinite(activeAnalysis.timingScore) && activeAnalysis.timingScore > 60
                              ? 'Subsequent Dasha Transition'
                              : 'Long-term Stabilizing Period'}
                          </strong>
                        </div>
                      </div>
                    </div>

                    {/* Important Caution / Condition */}
                    {typeof activeAnalysis.natalPromiseScore === 'number' && Number.isFinite(activeAnalysis.natalPromiseScore) && activeAnalysis.natalPromiseScore <= 75 && (
                      <div className="p-4 bg-orange-50/60 border border-orange-200 rounded-xl flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-700 shrink-0 mt-0.5" />
                        <div className="text-xs text-orange-950 space-y-1">
                          <strong className="font-bold block">Important Condition & Guidance:</strong>
                          <p className="leading-relaxed">
                            This period appears challenging or transitional. Extra caution, mindful planning, and avoiding hasty commitments are advisable.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* AI Contradiction Alert Banner */}
                    {activeAnalysis.aiContradictionDetected && (
                      <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3">
                        <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-950 space-y-1">
                          <strong className="font-bold block">Classical Baseline Mode (AI Commentary Suppressed):</strong>
                          <p className="leading-relaxed">
                            AI narrative contradicted server-verified astronomical chart placements. Unverified AI commentary has been suppressed to preserve astronomical accuracy. Displaying authentic classical Jyotish calculations.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Ascendant Boundary Sensitivity Warning */}
                    {activeAnalysis.isConfigurationSensitive && (
                      <div className="p-4 bg-sky-50 border border-sky-200 rounded-xl flex items-start gap-3">
                        <Info className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
                        <div className="text-xs text-sky-950 space-y-1">
                          <strong className="font-bold block">Ascendant Boundary Sensitivity:</strong>
                          <p className="leading-relaxed">
                            The recorded birth time is close to a zodiac sign boundary within the configured uncertainty window. Minor clock adjustments may alter the rising sign and divisional houses.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Expandable "Why?" */}
                    <div className="border-t border-astro-border pt-4">
                      <button
                        onClick={() => setShowWhy(!showWhy)}
                        className="inline-flex items-center gap-2 text-xs font-bold text-astro-secondary hover:text-[#263633] transition"
                      >
                        <HelpCircle className="w-4 h-4" />
                        <span>Why? (High-Level Summary)</span>
                        <ChevronRight className={`w-4 h-4 transition-transform ${showWhy ? 'rotate-90' : ''}`} />
                      </button>

                      <AnimatePresence>
                        {showWhy && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-3 p-4 bg-astro-secondary-surface rounded-xl border border-[#E2DED5] text-xs text-astro-secondary-text leading-relaxed space-y-2 overflow-hidden"
                          >
                            <p className="font-medium text-astro-text">
                              Your results are favourable because the principal life indicators, supporting divisional chart patterns, and current planetary timings are broadly aligned.
                            </p>
                            <ul className="list-disc pl-4 space-y-1 text-astro-secondary-text">
                              {activeAnalysis.whyPoints.slice(0, 3).map((pt, i) => (
                                <li key={i}>{pt}</li>
                              ))}
                            </ul>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Toggle Button for Detailed Technical Analysis */}
                    <div className="pt-2 flex justify-center border-t border-astro-border">
                      <button
                        onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                        className="px-5 py-2.5 rounded-xl bg-astro-secondary text-white font-bold text-xs hover:bg-amber-800 transition shadow flex items-center gap-2"
                      >
                        <Layers className="w-4 h-4" />
                        <span>{showTechnicalDetails ? 'Hide Technical Calculations' : 'View Detailed Technical Analysis (Calculations, Vargas, Shadbala)'}</span>
                      </button>
                    </div>
                  </div>

                  {/* ADVANCED TECHNICAL DETAILS — EXPOSED ONLY WHEN USER REQUESTS IT */}
                  {showTechnicalDetails && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-6 pt-2"
                    >
                      {/* Synthesis Metrics Header */}
                      <div className="bg-astro-bg border border-astro-border rounded-xl p-6 shadow-sm space-y-6">
                        <div className="border-b border-astro-border pb-3">
                          <span className="text-xs font-bold text-astro-secondary uppercase tracking-wider">
                            Technical Calculations & Multi-Model Metrics
                          </span>
                          <h3 className="text-lg font-bold text-astro-text mt-0.5">
                            Underlying Quantitative Evidence Scores
                          </h3>
                        </div>

                        {/* Dials / Meters */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="bg-astro-secondary-surface p-4 rounded-xl border border-[#E2DED5] flex flex-col justify-between">
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-[#263633] block">
                                Natal Promise (D1)
                              </span>
                              <p className="text-xs text-astro-secondary-text mt-1 leading-relaxed">
                                Primary foundation indicating whether the cosmic architecture supports this life event at all.
                              </p>
                            </div>
                            <div className="flex items-end justify-between mt-4">
                              <span className="text-2xl font-black text-astro-secondary font-mono">
                                {typeof activeAnalysis.natalPromiseScore === 'number' && Number.isFinite(activeAnalysis.natalPromiseScore)
                                  ? `${activeAnalysis.natalPromiseScore}%`
                                  : 'Fact-First'}
                              </span>
                              <span className="text-[10px] font-bold bg-astro-secondary/15 text-astro-secondary px-2 py-0.5 rounded uppercase">
                                {typeof activeAnalysis.natalPromiseScore === 'number' && Number.isFinite(activeAnalysis.natalPromiseScore)
                                  ? (activeAnalysis.natalPromiseScore > 80 ? 'Highly Promised' : 'Moderately Promised')
                                  : 'Classical Foundation'}
                              </span>
                            </div>
                          </div>

                          <div className="bg-astro-secondary-surface p-4 rounded-xl border border-[#E2DED5] flex flex-col justify-between">
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-[#263633] block">
                                Timing & Dasha Window
                              </span>
                              <p className="text-xs text-astro-secondary-text mt-1 leading-relaxed">
                                Activation readiness based on current and upcoming Vimshottari Mahadasha/Antardasha nodes.
                              </p>
                            </div>
                            <div className="flex items-end justify-between mt-4">
                              <span className="text-2xl font-black text-astro-secondary font-mono">
                                {typeof activeAnalysis.timingScore === 'number' && Number.isFinite(activeAnalysis.timingScore)
                                  ? `${activeAnalysis.timingScore}%`
                                  : 'Active Dasha'}
                              </span>
                              <span className="text-[10px] font-bold bg-astro-secondary/15 text-astro-secondary px-2 py-0.5 rounded uppercase">
                                {typeof activeAnalysis.timingScore === 'number' && Number.isFinite(activeAnalysis.timingScore)
                                  ? (activeAnalysis.timingScore > 75 ? 'Strong Activation' : 'Moderate Window')
                                  : 'Vimshottari Timeline'}
                              </span>
                            </div>
                          </div>

                          <div className="bg-astro-secondary-surface p-4 rounded-xl border border-[#E2DED5] flex flex-col justify-between">
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-[#263633] block">
                                Ensemble Confidence
                              </span>
                              <p className="text-xs text-astro-secondary-text mt-1 leading-relaxed">
                                Reliability consensus based on cross-model checks between Parashari, Jaimini, and Vargas.
                              </p>
                            </div>
                            <div className="flex items-end justify-between mt-4">
                              <span className="text-xl font-black text-astro-secondary font-mono">
                                {activeAnalysis.confidenceLevel}
                              </span>
                              <span className="text-[10px] font-bold bg-astro-secondary/15 text-astro-secondary px-2 py-0.5 rounded uppercase">
                                {activeAnalysis.confidenceLevel === 'HIGH' || activeAnalysis.confidenceLevel === 'VERY HIGH' ? 'Robust' : 'Conditional'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Three Levels of Evidence Visual Hierarchy */}
                      <div className="bg-astro-bg border border-astro-border rounded-xl p-5 shadow-sm space-y-4">
                        <h3 className="font-bold text-[#263633] text-sm flex items-center gap-1.5 border-b border-astro-border pb-2">
                          <Layers className="w-4 h-4 text-astro-secondary" /> Astrological Evidence Hierarchy
                        </h3>

                        <div className="space-y-3">
                          {/* Level 1: Primary */}
                          <div className="space-y-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-100 text-[10px] font-bold uppercase tracking-wider">
                              Level 1 — Primary Core (Natal Promise)
                            </span>
                            {activeAnalysis.evidenceList
                              .filter(e => e.level === 'PRIMARY')
                              .map((item, idx) => (
                                <div key={idx} className="p-3 bg-white border border-astro-border rounded-lg text-xs space-y-1">
                                  <div className="flex items-center justify-between">
                                    <strong className="text-astro-text font-bold">{item.factor}</strong>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                      item.strength === 'Strong' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'
                                    }`}>
                                      {item.strength}
                                    </span>
                                  </div>
                                  <p className="text-astro-secondary-text leading-relaxed">{item.evaluation}</p>
                                </div>
                              ))}
                          </div>

                          {/* Level 2: Secondary */}
                          <div className="space-y-2 pt-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-orange-50 text-orange-800 border border-orange-100 text-[10px] font-bold uppercase tracking-wider">
                              Level 2 — Secondary Refinement (Varga / Strength)
                            </span>
                            {activeAnalysis.evidenceList
                              .filter(e => e.level === 'SECONDARY')
                              .map((item, idx) => (
                                <div key={idx} className="p-3 bg-white border border-astro-border rounded-lg text-xs space-y-1">
                                  <div className="flex items-center justify-between">
                                    <strong className="text-astro-text font-bold">{item.factor}</strong>
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-astro-secondary-surface text-astro-secondary-text">
                                      {item.strength}
                                    </span>
                                  </div>
                                  <p className="text-astro-secondary-text leading-relaxed">{item.evaluation}</p>
                                </div>
                              ))}
                          </div>

                          {/* Level 3: Confirmatory */}
                          <div className="space-y-2 pt-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-100 text-[10px] font-bold uppercase tracking-wider">
                              Level 3 — Confirmatory Evidence (Transit / Jaimini)
                            </span>
                            {activeAnalysis.evidenceList
                              .filter(e => e.level === 'CONFIRMATORY')
                              .map((item, idx) => (
                                <div key={idx} className="p-3 bg-white border border-astro-border rounded-lg text-xs space-y-1">
                                  <div className="flex items-center justify-between">
                                    <strong className="text-astro-text font-bold">{item.factor}</strong>
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-astro-secondary-surface text-astro-secondary-text">
                                      {item.strength}
                                    </span>
                                  </div>
                                  <p className="text-astro-secondary-text leading-relaxed">{item.evaluation}</p>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>

                      {/* Jaimini vs Parashari Consensus Comparison Panel (When Jaimini or Integrated is selected) */}
                      {(mode === 'JAIMINI' || mode === 'INTEGRATED') && (
                        <div className="bg-astro-bg border border-astro-border rounded-xl p-5 shadow-sm space-y-4">
                          <h3 className="font-bold text-[#263633] text-sm flex items-center gap-1.5 border-b border-astro-border pb-2">
                            <TrendingUp className="w-4 h-4 text-astro-secondary" /> Jaimini & Parashari Independent Consensus
                          </h3>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-3.5 bg-white border border-astro-border rounded-lg space-y-1.5">
                              <span className="text-[10px] font-extrabold uppercase tracking-wider text-astro-secondary block">
                                Parashari Sastra Result
                              </span>
                              <p className="text-xs text-astro-secondary-text leading-relaxed">
                                Calculations based on 7th/10th Bhavesha placements, house aspects, and dasha transitions show positive manifestation of the natal promise.
                              </p>
                            </div>

                            <div className="p-3.5 bg-white border border-astro-border rounded-lg space-y-1.5">
                              <span className="text-[10px] font-extrabold uppercase tracking-wider text-astro-secondary block">
                                Jaimini Sutra Result
                              </span>
                              <p className="text-xs text-astro-secondary-text leading-relaxed">
                                Chara Darakaraka / Amatyakaraka coordinates with Arudha Lagna (AL) and Upapada (UL), aligning with public recognition.
                              </p>
                            </div>
                          </div>

                          <div className="p-3.5 bg-astro-secondary-surface border border-[#E2DED5] rounded-lg space-y-1 text-xs">
                            <span className="font-bold text-[#263633]">Common Ground Verdict:</span>
                            <p className="text-astro-secondary-text leading-relaxed">
                              Both systems agree that planetary activations during major transit crossings (Saturn and Jupiter double transit) trigger material success.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Predefined Analytical Frameworks Detail Tabs */}
                      <div className="bg-astro-bg border border-astro-border rounded-xl p-6 shadow-sm space-y-6">
                        <div className="flex flex-wrap gap-2 border-b border-astro-border pb-2">
                          {[
                            { id: 'promise', label: 'D1 Natal Foundation' },
                            { id: 'varga', label: 'Varga Refinement' },
                            { id: 'strength', label: 'Planetary Strength' },
                            { id: 'timing', label: 'Dasha Timing' },
                            { id: 'transit', label: 'Transit (Gochar)' },
                            { id: 'synthesis', label: 'Consensus Synthesis' }
                          ].map(tab => (
                            <button
                              key={tab.id}
                              onClick={() => setSubTab(tab.id as any)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                                subTab === tab.id
                                  ? 'bg-astro-secondary text-white shadow-sm'
                                  : 'text-astro-secondary-text hover:bg-astro-secondary-surface hover:text-astro-text'
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        <div className="text-xs text-astro-secondary-text leading-relaxed whitespace-pre-line prose max-w-none">
                          {subTab === 'promise' && (
                            <div className="space-y-4">
                              <h4 className="text-sm font-bold text-astro-text">D1 / Rashi Natal Promise Evaluation:</h4>
                              <p>{activeAnalysis.detailedAnalysis.d1Analysis}</p>
                              <div className="p-3 bg-astro-secondary-surface border border-astro-border rounded-lg">
                                <h5 className="font-bold text-[#263633] mb-1">Functional Benefics vs Malefics:</h5>
                                <p className="text-astro-secondary-text">
                                  For the ascendant ascendant lord, natural benefics can act as functional malefics if they rule Dusthanas (6/8/12). Our engine treats strength independently of functional results.
                                </p>
                              </div>
                            </div>
                          )}
                          {subTab === 'varga' && (
                            <div className="space-y-4">
                              <h4 className="text-sm font-bold text-astro-text">Divisional Varga Fine-tuning:</h4>
                              <p>{activeAnalysis.detailedAnalysis.vargaAnalysis}</p>
                              <div className="p-3 bg-astro-secondary-surface border border-astro-border rounded-lg">
                                <h5 className="font-bold text-[#263633] mb-1">Varga Mapping Key:</h5>
                                <p className="text-astro-secondary-text">
                                  D9 (Marriage), D10 (Profession), D2 (Wealth), D4 (Property), D7 (Children), D24 (Education), D20 (Spirituality), D6 (Health). Vargas do not override D1 promise.
                                </p>
                              </div>
                            </div>
                          )}
                          {subTab === 'strength' && (
                            <div className="space-y-4">
                              <h4 className="text-sm font-bold text-astro-text">Planetary & House Strength Indicators:</h4>
                              <p>{activeAnalysis.detailedAnalysis.strengthAnalysis}</p>
                              <div className="p-3 bg-astro-secondary-surface border border-astro-border rounded-lg">
                                <h5 className="font-bold text-[#263633] mb-1">Classical Strength Note:</h5>
                                <p className="text-astro-secondary-text">
                                  High Shadbala values represent absolute operational power, not goodness. A strong but functional difficult planet can manifest obstacles with intensity.
                                </p>
                              </div>
                            </div>
                          )}
                          {subTab === 'timing' && (
                            <div className="space-y-4">
                              <h4 className="text-sm font-bold text-astro-text">Vimshottari Dasha Timing Node:</h4>
                              <p>{activeAnalysis.detailedAnalysis.dashaTimingAnalysis}</p>
                            </div>
                          )}
                          {subTab === 'transit' && (
                            <div className="space-y-4">
                              <h4 className="text-sm font-bold text-astro-text">Gochar Transit Confirmations:</h4>
                              <p>{activeAnalysis.detailedAnalysis.transitConfirmation}</p>
                            </div>
                          )}
                          {subTab === 'synthesis' && (
                            <div className="space-y-4">
                              <h4 className="text-sm font-bold text-astro-text">Scholarly Synthesis:</h4>
                              <p>{activeAnalysis.detailedAnalysis.synthesis}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Gemstone Evaluator Section with Progressive Disclosure */}
                  <div className="bg-astro-bg border border-astro-border rounded-xl p-6 shadow-sm space-y-5">
                    <div className="border-b border-astro-border pb-3 flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-[#263633] text-base flex items-center gap-1.5">
                          <Gem className="text-astro-secondary" /> Gemstone Recommendation
                        </h3>
                        <p className="text-xs text-astro-secondary-text mt-1">
                          Calculated based on functional lordship and natal strength.
                        </p>
                      </div>
                      <span className="text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                        🟢 RECOMMENDED
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                          Select Planet to Strengthen
                        </label>
                        <select
                          value={gemSelectedPlanet}
                          onChange={e => setGemSelectedPlanet(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-astro-border rounded-lg text-xs"
                        >
                          <option value="Sun">Sun (Surya)</option>
                          <option value="Moon">Moon (Chandra)</option>
                          <option value="Mars">Mars (Mangal)</option>
                          <option value="Mercury">Mercury (Budh)</option>
                          <option value="Jupiter">Jupiter (Guru)</option>
                          <option value="Venus">Venus (Shukra)</option>
                          <option value="Saturn">Saturn (Shani)</option>
                        </select>
                      </div>

                      <div className="md:col-span-2 space-y-3 text-xs bg-astro-secondary-surface p-4 rounded-xl border border-[#E2DED5]">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-[#263633] text-sm">{gemEval.name}</span>
                          <button
                            onClick={() => setShowGemTechnical(!showGemTechnical)}
                            className="text-[11px] font-bold text-astro-secondary hover:underline"
                          >
                            {showGemTechnical ? 'Hide Technical Reasoning' : 'View Technical Reasoning'}
                          </button>
                        </div>
                        <p className="text-astro-text"><strong>High-Level Purpose:</strong> {gemEval.benefit}</p>
                        
                        {showGemTechnical && (
                          <div className="pt-2 border-t border-[#E2DED5] space-y-2 text-astro-secondary-text animate-fade-in">
                            <p><strong>Lordship Alignment:</strong> {gemEval.lordships}</p>
                            <p className="text-red-800 font-medium"><strong>Critical Risk Alert:</strong> {gemEval.risk}</p>
                            <p className="text-green-800 font-medium"><strong>Pure Non-Gem Alternative:</strong> {gemEval.nonGem}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Muhurta Engine Section with Progressive Disclosure */}
                  <div className="bg-astro-bg border border-astro-border rounded-xl p-6 shadow-sm space-y-5">
                    <div className="border-b border-astro-border pb-3 flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-[#263633] text-base flex items-center gap-1.5">
                          <Calendar className="text-astro-secondary" /> Auspicious Muhurta Selection
                        </h3>
                        <p className="text-xs text-astro-secondary-text mt-1">
                          Evaluates suitable timing windows for major life events.
                        </p>
                      </div>
                      <span className="text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                        🟢 HIGHLY FAVOURABLE
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                          Select Muhurta Activity
                        </label>
                        <select
                          value={muhurtaActivity}
                          onChange={e => setMuhurtaActivity(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-astro-border rounded-lg text-xs"
                        >
                          <option value="Marriage">Marriage (Vivaha)</option>
                          <option value="Property registration">Property Registration</option>
                          <option value="Business launch">Business / Shop Launch</option>
                          <option value="Vehicle purchase">Vehicle Purchase</option>
                        </select>
                      </div>

                      <div className="md:col-span-2 space-y-3 text-xs bg-astro-secondary-surface p-4 rounded-xl border border-[#E2DED5]">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-[#263633] text-sm">{muhurtaActivity} Suitability</span>
                          <button
                            onClick={() => setShowMuhurtaTechnical(!showMuhurtaTechnical)}
                            className="text-[11px] font-bold text-astro-secondary hover:underline"
                          >
                            {showMuhurtaTechnical ? 'Hide Panchanga Calculations' : 'View Panchanga Calculations'}
                          </button>
                        </div>
                        <p className="text-astro-text"><strong>Auspicious Tithis:</strong> {muhRules.tithis}</p>
                        
                        {showMuhurtaTechnical && (
                          <div className="pt-2 border-t border-[#E2DED5] space-y-2 text-astro-secondary-text animate-fade-in">
                            <p><strong>Recommended Nakshatras:</strong> {muhRules.naks}</p>
                            <p><strong>Ascendant / Lagna Rules:</strong> {muhRules.lagnas}</p>
                            <p className="text-red-800"><strong>Severe Prohibitions:</strong> {muhRules.prohibitions}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}
